import { z } from 'zod';

import {
  answerInputs,
  answerProblems,
  blankAnswers,
  type AnswerDrafts,
} from '@mds/shared/lib/form-answers';
import { picked, tooLong } from '@mds/shared/lib/validation';
import type {
  GuestEventFeedbackForm,
  SubmitEventFeedbackRequest,
} from '@mds/shared/types/event-feedback';
import type { FormQuestionSet } from '@mds/shared/types/form-question';

/**
 * Mirrors `submitEventFeedbackSchema` in the API's event-feedback module.
 *
 * The two standard fields every feedback form has are checked here. The
 * event's own questions are checked and converted by `lib/form-answers` — the
 * same rules the admin and the registration form use, mirroring the server's.
 */

/** The API's cap on the comment. */
const COMMENT_MAX = 5_000;

export type FeedbackFormValues = {
  overallRating: string;
  comment: string;
  /** Keyed by question id, which is what `FormQuestions` binds each field to. */
  answers: AnswerDrafts;
};

/** Blank, until the guest answers. */
export const feedbackDefaults = (form: FormQuestionSet): FeedbackFormValues => ({
  overallRating: '',
  comment: '',
  answers: blankAnswers(form),
});

/** The whole form, parsed straight into the request body. */
export const feedbackSchema = (form: Pick<GuestEventFeedbackForm, keyof FormQuestionSet>) =>
  z
    .object({
      // Always required, 1–5 — `standardFields.overallRating` is fixed by the API.
      overallRating: picked('Choose a score from 1 to 5.').transform(Number),
      comment: z.string().trim().max(COMMENT_MAX, tooLong(COMMENT_MAX)),
      answers: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
    })
    .superRefine((values, ctx) => {
      // Each problem lands under its own question, where `FormQuestions` shows it.
      for (const [questionId, message] of Object.entries(answerProblems(form, values.answers))) {
        ctx.addIssue({ code: 'custom', path: ['answers', questionId], message });
      }
    })
    .transform(
      (values): SubmitEventFeedbackRequest => ({
        overallRating: values.overallRating,
        ...(values.comment ? { comment: values.comment } : {}),
        answers: answerInputs(form, values.answers),
      }),
    );
